/**
 * DrillExplorer — the full-screen investigation view behind every card,
 * modeled on the client's DOMO drills (knowledge/design/domo/domo-12.png):
 *   - full-page takeover with a breadcrumb header, not a cramped modal
 *   - ONE BIG chart with a TYPE switcher (time: bars ⇄ line · categories:
 *     bars ⇄ donut) + chart ⇄ table toggle
 *   - RIGHT FILTER RAIL = the standard toggle set, identical on every drill:
 *     View by (Timeline/Product/State/Discount code) with Day-Week-Month
 *     grain on the timeline, Window incl. CUSTOM date range, Customer type,
 *     Order type, Channel, Refunds lens, Discount codes, Products, States —
 *     wired even where data is thin (channel shows 'Unknown' until
 *     attribution fills). A group that's meaningless for the current measure
 *     renders DISABLED with the reason — never silently hidden. Checklists
 *     start all-checked; Select all restores that (Jeremy 2026-06-10).
 *   - refunds are PRORATED by item revenue share in product views (order-
 *     level amounts against an item dimension — the standard BI treatment)
 *   - compare-vs-prior toggle (Δ column + footer), %-of-total, search, sort,
 *     CSV export, click-anything-to-drill-deeper, Esc to close
 *   - inherits the card's window keyword so totals reconcile with the card
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useApiClient } from "../lib/api-context.js";
import { buildProductHierarchy, buildProductSkus, buildSkuDisplayByCode, buildSkuToName, useProductSkusQuery, useResizableWidth, type ProductFamily } from "./ListControls.js";
import { Segmented } from "./Segmented.js";
import { TrendChart } from "./TrendChart.js";
import { DonutChart } from "./DonutChart.js";
import { ChartTypeMenu } from "./ChartTypeMenu.js";
import { PERIODS, usePeriod, type PeriodKey } from "../lib/period.js";
import { useDisplayOpts } from "../lib/display-opts.js";
import { WholesaleToggle } from "../lib/wholesale-toggle.js";
import { moneyFmt } from "./cards.js";
import { PageLoadError } from "./PageLoadError.js";
import { pageLoadState, type PageLoadState } from "../lib/page-load-state.js";
import { accountScopedStorageKey } from "../lib/account-browser-state.js";
import { useActiveAccountId } from "../lib/account-context.js";
import { readableSourceLabel } from "../lib/product-sku-label.js";

export type DrillMetric =
  | "revenue" | "cm" | "product_revenue" | "product_cm" | "aov" | "cm_aov" | "orders" | "customers" | "new_customers" | "units" | "boxes"
  | "refunded_amount" | "refunded_orders" | "refund_rate"
  | "chargeback_amount" | "chargeback_count" | "chargeback_rate" | "rebill_revenue" | "rebill_cm"
  | "subs_new" | "subs_canceled" | "subs_ended";
export type Dimension = "month" | "product" | "state" | "customer_type" | "channel" | "order_type" | "discount" | "time_of_day";
export type Granularity = "day" | "week" | "month";

export interface DrillWindow {
  key?: PeriodKey;
  from?: string;
  to?: string;
  label?: string;
}
export interface DrillFixedFilters {
  state?: string;
  product?: string;
  /** Product FAMILY (product_groups overlay) — opened from the Exec "Top
   *  products" card so the drill shows the products/SKUs within the family. */
  productGroup?: string;
  customerType?: "new" | "returning";
  orderType?: "one_time" | "subscription";
  channel?: string;
}
export interface DrillRequest {
  title: string;
  metric: DrillMetric;
  /** Set for a BLENDED RATIO card (MER/aMER/CAC). The drill then shows the ratio
   *  over time (RatioDrill) instead of slicing a single metric — clicking MER
   *  shows MER, not the revenue numerator (founder 2026-06-14). `metric` is still
   *  required by the type; pass the numerator metric (it's ignored for ratios). */
  ratio?: "mer" | "amer" | "cac" | "spend" | "cm-mer" | "cm-amer";
  /** For a per-CHANNEL ratio drill (CAC of one channel over time). */
  ratioChannel?: string;
  dimension?: Dimension;
  /** Optional initial product grain. Normal card drills start at Products. */
  grouping?: "product" | "sku";
  period?: DrillWindow;
  filters?: DrillFixedFilters;
  /** Card key for the chart-type preference (defaults to metric). Lets a card's
   *  drill remember its own chart type per card (2026-06-13). */
  chartKey?: string;
  /** Set for a Customer LTV card. Opens the per-segment LTV detail (LtvDrill) —
   *  its curve + cohort, filterable/combinable by first-order channel / affiliate
   *  / discount / product. `metric` is still required by the type but ignored. */
  ltv?: { channel?: string; affiliate?: string; discount?: string; product?: string };
  /** Set to render arbitrary content in the drill modal (founder 2026-07-14): the
   *  "View by channel" links open the by-channel breakdown as a real drill-down
   *  instead of an inline card. `metric` is still required by the type but ignored. */
  node?: React.ReactNode;
}

interface Row {
  key: string;
  revenue: number;
  cm: number;
  orders: number;
  customers: number;
  aov: number;
  units: number;
  value: number;
  prev?: number;
}

// View-by options. Channel is a first-class view-by now (founder's blended model
// 2026-07-13: channel moved OFF the top-level rail and INTO the drill — "to see a
// number by channel, open a card and switch View by to Channel"). It coexists with
// the channel FILTER group below: view BY channel, and/or filter TO channels, the
// same way product does. Customer type / order type stay filter-only (rarely a
// useful primary axis); the backend supports them as dimensions if ever needed.
const DIMS: Array<{ value: Dimension; label: string }> = [
  { value: "month", label: "Timeline" },
  { value: "product", label: "Product" },
  { value: "channel", label: "Channel" },
  { value: "state", label: "State" },
  { value: "discount", label: "Discount code" },
  { value: "time_of_day", label: "Time of day" },
];

/** Chronological order for daypart keys — never value-sort a clock. */
const PART_ORDER: Record<string, number> = {
  "Morning (6am–12pm)": 0, "Afternoon (12–5pm)": 1, "Evening (5–10pm)": 2, "Night (10pm–6am)": 3,
};

const METRIC_META: Record<DrillMetric, { label: string; money: boolean; percent?: boolean; additive: boolean }> = {
  revenue: { label: "Revenue", money: true, additive: true },
  cm: { label: "Contribution margin", money: true, additive: true },
  product_revenue: { label: "Product revenue", money: true, additive: true },
  product_cm: { label: "Product CM", money: true, additive: true },
  aov: { label: "Avg order value", money: true, additive: false },
  cm_aov: { label: "Avg contribution margin per order", money: true, additive: false },
  orders: { label: "Orders", money: false, additive: true },
  customers: { label: "Customers", money: false, additive: false },
  new_customers: { label: "New customers", money: false, additive: true },
  units: { label: "Products sold", money: false, additive: true },
  boxes: { label: "Individual units", money: false, additive: true },
  refunded_amount: { label: "Refunded", money: true, additive: true },
  refunded_orders: { label: "Refunded orders", money: false, additive: true },
  refund_rate: { label: "Refund rate", money: false, percent: true, additive: false },
  chargeback_amount: { label: "Chargebacks", money: true, additive: true },
  chargeback_count: { label: "Chargeback count", money: false, additive: true },
  chargeback_rate: { label: "Chargeback rate", money: false, percent: true, additive: false },
  rebill_revenue: { label: "Subscription revenue", money: true, additive: true },
  rebill_cm: { label: "Subscription CM", money: true, additive: true },
  subs_new: { label: "New subscriptions", money: false, additive: true },
  subs_canceled: { label: "Canceled subscriptions", money: false, additive: true },
  subs_ended: { label: "Ended subscriptions", money: false, additive: true },
};

/** Mirror of the backend's validDimensions() — the rail only offers these. */
function validDims(metric: DrillMetric): Dimension[] {
  if (metric === "subs_new" || metric === "subs_canceled" || metric === "subs_ended") return ["month", "state", "product", "time_of_day"];
  return DIMS.map((d) => d.value);
}

/** Plan-flow metrics: order-level filters don't apply (plans aren't orders). */
const isSubsMetric = (m: DrillMetric): boolean => m === "subs_new" || m === "subs_canceled" || m === "subs_ended";

const CONTEXT_COLS: DrillMetric[] = ["revenue", "orders", "aov", "customers", "units"];

const fmtMetric = (m: DrillMetric, n: number): string =>
  METRIC_META[m].percent ? `${n.toFixed(1)}%` : METRIC_META[m].money ? moneyFmt(n) : Math.round(n).toLocaleString();

function cell(row: Row, col: DrillMetric): number {
  switch (col) {
    case "revenue": return row.revenue;
    case "cm": return row.cm;
    case "orders": return row.orders;
    case "aov": return row.aov;
    case "customers": return row.customers;
    case "units": return row.units;
    default: return row.value;
  }
}

/** A saved snapshot of the drill's rail state — measure-independent. */
interface ViewPreset {
  name: string;
  dim: Dimension;
  grain: Granularity;
  win: DrillWindow | null;
  products: string[];
  states: string[];
  channels: string[];
  discounts: string[];
  customerType: "" | "new" | "returning";
  orderType: "" | "one_time" | "subscription";
  refunded: "" | "exclude" | "only";
}

export function drillPresetsStorageKey(accountId: string): string {
  return accountScopedStorageKey(accountId, "drill-presets-v1");
}

function loadPresets(accountId: string): ViewPreset[] {
  try {
    const raw = localStorage.getItem(drillPresetsStorageKey(accountId));
    const parsed = raw ? (JSON.parse(raw) as ViewPreset[]) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 20) : [];
  } catch {
    // Corrupted storage — start fresh rather than crash the drill.
    return [];
  }
}
function storePresets(accountId: string, p: ViewPreset[]): void {
  try {
    localStorage.setItem(drillPresetsStorageKey(accountId), JSON.stringify(p));
  } catch {
    // Storage full/blocked — presets just won't persist; nothing to break.
  }
}

export function DrillExplorer({ req, onClose, originLabel = "Dashboard" }: { req: DrillRequest; onClose: () => void; originLabel?: string }): React.ReactElement {
  const api = useApiClient();
  const browserAccountId = useActiveAccountId() ?? "public-demo";
  const productSkusQ = useProductSkusQuery();
  const productSkus = useMemo(() => buildProductSkus(productSkusQ.data), [productSkusQ.data]);
  const skuToName = useMemo(() => buildSkuToName(productSkus), [productSkus]);
  const skuDisplayByCode = useMemo(() => buildSkuDisplayByCode(productSkusQ.data), [productSkusQ.data]);
  const productHierarchy = useMemo(() => buildProductHierarchy(productSkusQ.data), [productSkusQ.data]);
  const productSkusLoad = pageLoadState(productSkusQ);
  // The SKU code remains the row key, but the person sees the offer first and the
  // exact source code second. Every click/filter/export therefore stays stable.
  const rowLabel = (key: string): string => {
    if (dim !== "product" || grouping !== "sku") return key;
    const display = skuDisplayByCode[key];
    if (display) return [display.description, display.details, display.code].filter(Boolean).join(" · ");
    return skuToName[key] ? `${skuToName[key]} · SKU ${key}` : `SKU ${key}`;
  };
  const { width: railW, onGripDown } = useResizableWidth(320, "dd-drill-rail-w-v2", 250, 780);
  // Collapse the filter rail to a thin strip to give the chart full width
  // (founder 2026-06-28: "collapse the sidebar on both sides"). Persisted.
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem("dd-drill-rail-collapsed") === "1"; } catch { return false; }
  });
  const toggleRail = (): void => setRailCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem("dd-drill-rail-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
    return next;
  });
  // The measure is the card you clicked — fixed. (A switcher here tested as
  // "takes me to a completely different card" — removed per Jeremy.)
  const metric = req.metric;
  const meta = METRIC_META[metric];
  const dims = validDims(metric);
  const subsMetric = isSubsMetric(metric);
  // The drill inherits the GLOBAL timeline by default (founder 2026-06-13:
  // "changing the timeline wasn't updating the cards"). Openers that don't pass
  // an explicit req.period — most card drills — now open on the same window the
  // user picked up top, instead of silently showing all-time. The rail's
  // "Window" group still lets them widen to All history per-drill.
  const gp = usePeriod();
  // Inherit the global window — {key} for a fixed period, {from,to} for a custom
  // calendar range (the query builder sends from/to when there's no key). (#3)
  const defaultWin = (): DrillWindow => gp.drillWindow;
  const [dim, setDim] = useState<Dimension>(req.dimension && dims.includes(req.dimension) ? req.dimension : "month");
  // Product drills lump by family by default; flip to SKU to "drill down further IF
  // needed" (founder 2026-06-26). Only applies to the product dimension.
  const [grouping, setGrouping] = useState<"sku" | "product">(req.grouping ?? "product");
  // The product FILTER's leaf level (sidebar) — independent of the row grouping, so you
  // can view by product but filter by SKU. Switching it clears the selection.
  const [filterGrouping, setFilterGrouping] = useState<"product" | "sku">("product");
  const [grain, setGrain] = useState<Granularity>("month");
  const [todGrain, setTodGrain] = useState<"part" | "hour">("part");
  const [win, setWin] = useState<DrillWindow | null>(req.period ?? defaultWin());
  const [products, setProducts] = useState<string[]>(req.filters?.product ? [req.filters.product] : []);
  // Product family is a FIXED filter the drill opened with (from the Top
  // products card) — not an editable rail. Carried straight through to the query.
  const productGroup = req.filters?.productGroup;
  const [states, setStates] = useState<string[]>(req.filters?.state ? [req.filters.state] : []);
  const [customerType, setCustomerType] = useState<"" | "new" | "returning">(req.filters?.customerType ?? "");
  const [orderType, setOrderType] = useState<"" | "one_time" | "subscription">(req.filters?.orderType ?? "");
  const [channels, setChannels] = useState<string[]>(req.filters?.channel ? [req.filters.channel] : []);
  const [discounts, setDiscounts] = useState<string[]>([]);
  const [refunded, setRefunded] = useState<"" | "exclude" | "only">("");
  // Tax / shipping basis now come from the global gross↔net control (one source
  // of truth, shared with the cards) — not a per-drill toggle.
  const display = useDisplayOpts();
  const includeTaxes = display.tax === "include";
  const includeShipping = display.shipping === "include";
  // Chart-type prefs, persisted per card (founder 2026-06-13: "choose the chart
  // type on both views and save it"). Time vs category remembered separately;
  // the rolling-average overlay too. Base key shared with the card so the drill
  // opens on a sensible default.
  const chartBase = req.chartKey ?? req.metric;
  const dlGet = (k: string, d: string): string => { try { return localStorage.getItem(`dd-chart-drill-${chartBase}-${k}`) ?? d; } catch { return d; } };
  const dlSet = (k: string, v: string): void => { try { localStorage.setItem(`dd-chart-drill-${chartBase}-${k}`, v); } catch { /* in-memory */ } };
  const [view, setViewRaw] = useState<"chart" | "table">(dlGet("view", "chart") === "table" ? "table" : "chart");
  // Default to a LINE trend for time series (founder: "no trendlines when you
  // drill in"); Area/Column/Step are one dropdown-click away.
  const [timeKind, setTimeKindRaw] = useState<"line" | "area" | "column" | "step">((["line", "area", "column", "step"].includes(dlGet("time", "line")) ? dlGet("time", "line") : "line") as "line" | "area" | "column" | "step");
  // Product drills default to donut/pie — best way to see share of sales (founder
  // 2026-06-22); other category dims keep bars unless the user saved a preference.
  const [catKind, setCatKindRaw] = useState<"bars" | "donut">(dlGet("cat", req.dimension === "product" ? "donut" : "bars") === "donut" ? "donut" : "bars");
  const [drillAvg, setDrillAvgRaw] = useState<boolean>(dlGet("avg", "1") === "1");
  const setView = (v: "chart" | "table"): void => { setViewRaw(v); dlSet("view", v); };
  const setTimeKind = (v: "line" | "area" | "column" | "step"): void => { setTimeKindRaw(v); dlSet("time", v); };
  const setCatKind = (v: "bars" | "donut"): void => { setCatKindRaw(v); dlSet("cat", v); };
  const setDrillAvg = (v: boolean): void => { setDrillAvgRaw(v); dlSet("avg", v ? "1" : "0"); };
  const [presets, setPresets] = useState<ViewPreset[]>(() => loadPresets(browserAccountId));
  const [presetName, setPresetName] = useState("");
  const [compareMode, setCompareMode] = useState<"prior" | "prior_year">("prior");
  const [compare, setCompare] = useState(false);
  const [sortCol, setSortCol] = useState<DrillMetric | "key">(metric);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const compareAvailable = dim !== "month" && Boolean(win && (win.key ? win.key !== "all" : win.from && win.to));
  const compareOn = compare && compareAvailable;

  const query = useMemo(() => {
    const q: Record<string, string | string[]> = { metric, dimension: dim };
    if (dim === "month" && grain !== "month") q.granularity = grain;
    if (dim === "time_of_day" && todGrain === "hour") q.todGrain = "hour";
    if (win?.key) q.period = win.key;
    else if (win?.from) { q.from = win.from; if (win.to) q.to = win.to; }
    if (dim === "product" && grouping === "sku") q.grouping = "sku";
    if (products.length) q.product = products;
    if (productGroup) q.productGroup = productGroup;
    if (channels.length) q.channel = channels;
    if (states.length) q.state = states;
    if (!subsMetric) {
      if (customerType) q.customerType = customerType;
      if (orderType) q.orderType = orderType;
      if (refunded) q.refunded = refunded;
      if (discounts.length) q.discount = discounts;
      if (includeTaxes) q.includeTaxes = "true";
      if (!includeShipping) q.includeShipping = "false";
    }
    if (compareOn) q.compare = compareMode;
    return q;
  }, [metric, dim, grouping, grain, todGrain, win, products, productGroup, states, customerType, orderType, channels, refunded, discounts, includeTaxes, includeShipping, compareOn, compareMode, subsMetric]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["breakdown", query],
    queryFn: () => api.get<{ rows: Row[] }>("/dashboard/breakdown", query),
  });


  // Rail option lists (all-time, cached across drill opens). Products come from the
  // catalog hierarchy (useProductHierarchy) instead of a flat breakdown list now.
  const stateList = useQuery({
    queryKey: ["drill-options", "state"],
    queryFn: () => api.get<{ rows: Array<{ key: string }> }>("/dashboard/breakdown", { metric: "orders", dimension: "state" }),
  });
  const channelList = useQuery({
    queryKey: ["drill-options", "channel"],
    queryFn: () => api.get<{ rows: Array<{ key: string }> }>("/dashboard/breakdown", { metric: "orders", dimension: "channel" }),
  });
  const discountList = useQuery({
    queryKey: ["drill-options", "discount"],
    queryFn: () => api.get<{ rows: Array<{ key: string }> }>("/dashboard/breakdown", { metric: "orders", dimension: "discount" }),
  });

  const rows = data?.rows ?? [];
  const skuLabelsRequired = dim === "product" && grouping === "sku";
  // Plan-by-product is NOT sum-safe: a plan containing two products appears
  // under both, so the footer total / %-of-total / donut all hide there.
  const isAdditive = meta.additive && !(subsMetric && dim === "product");
  const total = isAdditive ? rows.reduce((s, r) => s + r.value, 0) : null;
  const prevTotal = isAdditive && compareOn ? rows.reduce((s, r) => s + (r.prev ?? 0), 0) : null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? rows.filter((r) => `${r.key} ${rowLabel(r.key)}`.toLowerCase().includes(q)) : rows.slice();
    // A clock reads in clock order — keep dayparts/hours chronological and
    // let the VALUES vary, instead of ranking by value like other categories.
    if (dim === "time_of_day" && sortCol === metric) {
      base.sort((a, b) => (PART_ORDER[a.key] ?? 99) - (PART_ORDER[b.key] ?? 99) || a.key.localeCompare(b.key));
      return base;
    }
    base.sort((a, b) => {
      const av = sortCol === "key" ? a.key : cell(a, sortCol);
      const bv = sortCol === "key" ? b.key : cell(b, sortCol);
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av).localeCompare(String(bv));
        return sortDir === "asc" ? cmp : -cmp;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return base;
  }, [rows, search, sortCol, sortDir, dim, metric, grouping, skuDisplayByCode, skuToName]);

  const barCap = dim === "time_of_day" ? 24 : 14;
  const barRows = filtered.slice(0, barCap);
  const otherRows = filtered.slice(barCap);
  const otherSum = isAdditive ? otherRows.reduce((s, r) => s + r.value, 0) : 0;
  const barMax = Math.max(1, ...barRows.map((r) => Math.abs(r.value)), Math.abs(otherSum));
  // Count-metric drills (units, orders, customers) show COUNT context only —
  // no dollar columns (founder 2026-06-17: a "units" table reading as dollars).
  // Money-metric drills keep the full context.
  const contextCols = subsMetric
    ? []
    : CONTEXT_COLS.filter((c) =>
        c !== metric &&
        !(metric === "product_revenue" && c === "revenue") &&
        (meta.money || !METRIC_META[c].money));

  const drillInto = (row: Row): void => {
    // No time-of-day FILTER exists (only the view) — a daypart row is a leaf.
    if (dim === "time_of_day") return;
    const next = (preferred: Dimension): Dimension =>
      dims.includes(preferred) ? preferred : dims.find((d) => d !== dim) ?? dim;
    if (dim === "month") {
      // Key is a month (YYYY-MM) or a day/week-start (YYYY-MM-DD by grain).
      const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(row.key);
      if (day) {
        const start = new Date(`${row.key}T00:00:00Z`);
        const end = new Date(start.getTime() + (grain === "week" ? 7 : 1) * 86_400_000);
        setWin({ from: row.key, to: end.toISOString().slice(0, 10), label: row.key });
        setDim(next("product"));
        return;
      }
      const m = /^(\d{4})-(\d{2})$/.exec(row.key);
      if (!m) return;
      const y = Number(m[1]); const mo = Number(m[2]);
      const nextY = mo === 12 ? y + 1 : y;
      const nextMo = mo === 12 ? 1 : mo + 1;
      setWin({ from: `${m[1]}-${m[2]}-01`, to: `${nextY}-${String(nextMo).padStart(2, "0")}-01`, label: row.key });
      setDim(next("product"));
      return;
    }
    // SKU rows are the leaf — a sku_code can't filter the family-keyed query, so no
    // further drill (the SKU breakdown IS the detail the founder wanted).
    if (dim === "product" && grouping === "sku") return;
    if (dim === "product") { setProducts((p) => (p.includes(row.key) ? p : [...p, row.key])); setDim("month"); }
    else if (dim === "state") { setStates((s) => (s.includes(row.key) ? s : [...s, row.key])); setDim(next("product")); }
    else if (dim === "discount") { setDiscounts((d) => (d.includes(row.key) ? d : [...d, row.key])); setDim(next("product")); }
  };
  const drillIntoKey = (key: string): void => {
    const row = filtered.find((r) => r.key === key);
    if (row) drillInto(row);
  };

  const toggleSort = (col: DrillMetric | "key"): void => {
    if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(col); setSortDir(col === "key" ? "asc" : "desc"); }
  };

  const exportCsv = (): void => {
    const cols: DrillMetric[] = [metric, ...contextCols];
    const header = ["key", ...cols.map((c) => METRIC_META[c].label), ...(compareOn ? ["Prior"] : [])];
    const lines = [header.join(",")];
    for (const r of filtered) {
      lines.push([
        csvCell(r.key),
        ...cols.map((c) => String(Math.round(cell(r, c) * 100) / 100)),
        ...(compareOn ? [String(Math.round((r.prev ?? 0) * 100) / 100)] : []),
      ].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${metric}-by-${dim}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount =
    products.length + states.length + channels.length +
    // Order-only lenses aren't applied to plan metrics — don't count them
    // while they're inactive (the chip would point at nothing).
    (subsMetric ? 0 : (customerType ? 1 : 0) + (orderType ? 1 : 0) + (refunded ? 1 : 0) + discounts.length);
  const clearAll = (): void => {
    setProducts([]); setStates([]); setCustomerType(""); setOrderType(""); setChannels([]); setRefunded(""); setDiscounts([]);
    setWin(req.period ?? defaultWin());
  };

  const saveCurrentPreset = (): void => {
    const name = presetName.trim().slice(0, 40);
    if (!name) return;
    const snapshot: ViewPreset = {
      name, dim, grain, win, products, states, channels, discounts,
      customerType, orderType, refunded,
    };
    const next = [snapshot, ...presets.filter((p) => p.name !== name)].slice(0, 20);
    setPresets(next);
    storePresets(browserAccountId, next);
    setPresetName("");
  };
  const applyPreset = (p: ViewPreset): void => {
    setDim(dims.includes(p.dim) ? p.dim : "month");
    setGrain(p.grain ?? "month");
    setWin(p.win ?? null);
    setProducts(p.products ?? []);
    setStates(p.states ?? []);
    setChannels(p.channels ?? []);
    setDiscounts(p.discounts ?? []);
    setCustomerType(p.customerType ?? "");
    setOrderType(p.orderType ?? "");
    setRefunded(p.refunded ?? "");
    setSearch("");
  };
  const deletePreset = (name: string): void => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next);
    storePresets(browserAccountId, next);
  };

  const share = (v: number): string => (total && total > 0 ? `${((v / total) * 100).toFixed(1)}%` : "—");
  const deltaPct = (curV: number, prevV: number | undefined): React.ReactNode => {
    if (prevV === undefined) return null;
    if (prevV === 0) return <span style={{ color: "var(--faint)" }}>new</span>;
    const d = (curV - prevV) / prevV;
    return (
      <span className="dd-num" style={{ color: d >= 0 ? "var(--good)" : "var(--bad)", fontWeight: 600 }}>
        {d >= 0 ? "▲" : "▼"} {Math.abs(d * 100).toFixed(0)}%
      </span>
    );
  };

  const donutAvailable = dim !== "month" && isAdditive;
  const dimTime = dim === "month";
  // One compact dropdown for chart type (founder 2026-06-13: "a dropdown, not
  // taking up a bunch of room") on the drill too, with Table folded in and a
  // rolling-average toggle for time charts. Choice persists per card.
  const chartMenuOptions = dimTime
    ? [{ value: "line", label: "Line" }, { value: "area", label: "Area" }, { value: "column", label: "Column" }, { value: "step", label: "Step line" }, { value: "table", label: "Table" }]
    : donutAvailable
      ? [{ value: "bars", label: "Bars" }, { value: "donut", label: "Donut" }, { value: "table", label: "Table" }]
      : [{ value: "bars", label: "Bars" }, { value: "table", label: "Table" }];
  const chartMenuValue = view === "table" ? "table" : dimTime ? timeKind : catKind;
  const onChartMenu = (v: string): void => {
    if (v === "table") { setView("table"); return; }
    setView("chart");
    if (dimTime) setTimeKind(v as "line" | "area" | "column" | "step");
    else setCatKind(v as "bars" | "donut");
  };
  const chartMenu = (
    <ChartTypeMenu value={chartMenuValue} options={chartMenuOptions} onChange={onChartMenu}
      avg={drillAvg} onToggleAvg={setDrillAvg} avgApplies={dimTime && view === "chart"} />
  );

  return (
    <div style={overlay} role="dialog" aria-modal="true">
      {/* Header: breadcrumb + actions */}
      <div style={headerBar}>
        <div style={{ minWidth: 0 }}>
          <div style={crumb}>
            <button onClick={onClose} style={crumbLink}>{originLabel}</button>
            <span style={{ color: "var(--faint)" }}> › </span>
            <span style={{ color: "var(--text)", fontWeight: 600 }}>{req.title}</span>
            {isFetching && <span style={{ color: "var(--faint)" }}> · updating…</span>}
          </div>
          <h2 style={{ margin: "2px 0 0", fontSize: 21 }}>
            {meta.label} by {dim === "month" ? (grain === "month" ? "month" : grain) : dimLabel(dim).toLowerCase()}
          </h2>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
          {dim === "product" && (
            <Segmented size="sm" ariaLabel="Group products by"
              options={[{ value: "product", label: "Products" }, { value: "sku", label: "SKUs" }]}
              value={grouping} onChange={(v) => setGrouping(v as "sku" | "product")} />
          )}
          {compareAvailable && (
            <Segmented size="sm" ariaLabel="Compare"
              options={[{ value: "off", label: "Compare off" }, { value: "prior", label: "vs prior period" }, { value: "prior_year", label: "vs last year" }]}
              value={compare ? compareMode : "off"}
              onChange={(v) => {
                if (v === "off") setCompare(false);
                else { setCompare(true); setCompareMode(v as "prior" | "prior_year"); }
              }} />
          )}
          {chartMenu}
          <button onClick={exportCsv} style={ghostBtn}>Export CSV</button>
          <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>
        </div>
      </div>

      {/* Window control — promoted OUT of the filter rail to a bar at the top,
          above the body (founder 2026-06-14: the timeline is technically a
          filter but belongs up top, not buried in the filter list). Drives the
          same `win` state the breakdown query reads. */}
      <div style={windowBar}>
        <span style={{ ...railLabel, marginBottom: 0 }}>Window</span>
        <Segmented size="sm" ariaLabel="Window"
          options={PERIODS.map((p) => ({ value: p.value, label: p.label }))}
          value={(win?.key ?? "") as PeriodKey}
          onChange={(v) => setWin({ key: v, label: PERIODS.find((p) => p.value === v)?.label ?? v })} />
        <CustomRange horizontal win={win} onApply={(from, to, label) => setWin({ from, to, label })} />
      </div>

      <div style={bodyRow}>
        {/* Main pane */}
        <div style={{ flex: 1, minWidth: 0, padding: "18px 22px", overflowY: "auto" }}>
          {skuLabelsRequired && productSkusLoad.failed ? (
            <PageLoadError
              title="We couldn't load your product list"
              retrying={productSkusLoad.retrying}
              onRetry={productSkusLoad.retry}
            />
          ) : skuLabelsRequired && productSkusQ.isLoading ? (
            <div role="status" style={centerBox}><span style={{ color: "var(--faint)" }}>Loading product names…</span></div>
          ) : isError ? (
            <div style={centerBox}>
              <div style={{ color: "var(--bad)", fontWeight: 600, marginBottom: 8 }}>Couldn’t load this view.</div>
              <button onClick={() => void refetch()} style={ghostBtn}>Retry</button>
            </div>
          ) : isLoading ? (
            <Skeleton />
          ) : rows.length === 0 ? (
            <div style={centerBox}><span style={{ color: "var(--faint)" }}>No rows match these filters.</span></div>
          ) : view === "chart" && dim === "month" ? (
            <div style={chartCard}>
              <TrendChart kind={timeKind === "area" ? "area" : timeKind === "step" ? "step" : timeKind === "column" ? "stacked-bars" : "line"}
                money={meta.money} percent={meta.percent} showValues={timeKind === "column"} rollingAvg={drillAvg}
                pace={grain === "month" && meta.additive && !meta.percent}
                points={[...filtered].sort((a, b) => a.key.localeCompare(b.key)).map((r) => ({ label: r.key, a: r.value }))} />
            </div>
          ) : view === "chart" && catKind === "donut" && donutAvailable ? (
            <div style={chartCard}>
              <DonutChart money={meta.money} onPick={drillIntoKey}
                slices={filtered.map((r) => ({ key: r.key, value: r.value, label: rowLabel(r.key) }))} />
            </div>
          ) : view === "chart" ? (
            <div style={chartCard}>
              {barRows.map((r) => (
                <div key={r.key} onClick={() => drillInto(r)} style={barRow} className="dd-drill-row" title="Click to drill in">
                  <span style={barLabel}>{rowLabel(r.key)}</span>
                  <div style={barTrack}>
                    <div style={{ width: `${Math.max(1.5, (Math.abs(r.value) / barMax) * 100)}%`, height: "100%", background: "var(--accent)", borderRadius: 6 }} />
                  </div>
                  {compareOn && <span style={{ width: 64, textAlign: "right", fontSize: 11.5 }}>{deltaPct(r.value, r.prev)}</span>}
                  {isAdditive && <span className="dd-num" style={shareCol}>{share(r.value)}</span>}
                  <span className="dd-num" style={barVal}>{fmtMetric(metric, r.value)}</span>
                </div>
              ))}
              {otherRows.length > 0 && isAdditive && (
                <div style={{ ...barRow, cursor: "default", opacity: 0.7 }} title={`${otherRows.length} more — switch to Table to see all`}>
                  <span style={barLabel}>Other ({otherRows.length.toLocaleString()})</span>
                  <div style={barTrack}>
                    <div style={{ width: `${Math.max(1.5, (Math.abs(otherSum) / barMax) * 100)}%`, height: "100%", background: "var(--body-faint)", opacity: 0.5, borderRadius: 6 }} />
                  </div>
                  {compareOn && <span style={{ width: 64 }} />}
                  <span className="dd-num" style={shareCol}>{share(otherSum)}</span>
                  <span className="dd-num" style={barVal}>{fmtMetric(metric, otherSum)}</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${dim === "customer_type" ? "type" : dimLabel(dim).toLowerCase()}…`} style={searchStyle} spellCheck={false} />
              <div style={{ overflow: "auto", marginTop: 10, maxHeight: "calc(100vh - 280px)", border: "1px solid var(--hair)", borderRadius: 8 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <Th onClick={() => toggleSort("key")} active={sortCol === "key"} dir={sortDir} align="left">{dim === "month" ? (grain === "day" ? "Day" : grain === "week" ? "Week" : "Month") : dimLabel(dim)}</Th>
                      <Th onClick={() => toggleSort(metric)} active={sortCol === metric} dir={sortDir}>{meta.label}</Th>
                      {isAdditive && <Th onClick={() => toggleSort(metric)} active={false} dir={sortDir}>% of total</Th>}
                      {compareOn && <Th onClick={() => toggleSort(metric)} active={false} dir={sortDir}>vs prior</Th>}
                      {contextCols.map((col) => (
                        <Th key={col} onClick={() => toggleSort(col)} active={sortCol === col} dir={sortDir}>{METRIC_META[col].label}</Th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => (
                      <tr key={r.key} onClick={() => drillInto(r)} style={{ cursor: "pointer" }} className="dd-drill-row">
                        <td style={tdKey} title={r.key}>{rowLabel(r.key)}</td>
                        <td className="dd-num" style={{ ...td, color: "var(--num)", fontWeight: 700 }}>{fmtMetric(metric, r.value)}</td>
                        {isAdditive && <td className="dd-num" style={{ ...td, color: "var(--num)" }}>{share(r.value)}</td>}
                        {compareOn && <td style={td}>{deltaPct(r.value, r.prev)}</td>}
                        {contextCols.map((col) => (
                          <td key={col} className="dd-num" style={td}>{fmtMetric(col, cell(r, col))}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!isLoading && !isError && rows.length > 0 && (
            <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, color: "var(--body-faint)", fontSize: 11.5 }}>
              <span>
                {filtered.length.toLocaleString()} {filtered.length === 1 ? "row" : "rows"}
                {total !== null && <> · total {fmtMetric(metric, total)}{win?.key && win.label ? ` for ${win.label.toLowerCase()}` : ""}</>}
                {prevTotal !== null && prevTotal > 0 && total !== null && <> · {deltaPct(total, prevTotal)} vs prior</>}
              </span>
              <span>
                {dim === "channel"
                  ? "“Unknown” = orders we can’t tie to a channel yet — fills in as attribution data lands."
                  : refunded && (dim === "product" || products.length > 0)
                    ? "Refund lens includes WHOLE orders (refunds are order-level) — every product in a refunded order counts."
                    : (dim === "product" || products.length > 0)
                      ? "Product revenue is item price × quantity (before tax/shipping)."
                      : "Click any row, bar, or slice to drill deeper."}
              </span>
            </div>
          )}
        </div>

        {/* Filter rail — the standard toggle set, identical on every drill.
            Collapsible to a thin strip to give the chart full width (founder 2026-06-28). */}
        {railCollapsed ? (
          <aside style={railStrip} onClick={toggleRail} title="Show filters" role="button" aria-label="Show filters">
            <PanelRightOpen size={18} strokeWidth={1.8} style={{ color: "var(--body)" }} />
            <span style={railStripLabel}>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}</span>
          </aside>
        ) : (
        <aside style={{ ...rail, width: railW, position: "relative" }}>
          <RailGrip onGripDown={onGripDown} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
            <span style={railTitle}>Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {(activeFilterCount > 0 || (win && !win.key)) && <button onClick={clearAll} style={clearAllBtn}>Clear all</button>}
              <button onClick={toggleRail} style={railIconBtn} title="Hide filters" aria-label="Hide filters">
                <PanelRightClose size={16} strokeWidth={1.8} />
              </button>
            </div>
          </div>

          <RailGroup label="Saved views">
            {presets.map((p) => (
              <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button onClick={() => applyPreset(p)} className="dd-drill-row" style={{ ...checkRow, flex: 1, width: "auto", margin: 0 }} title={`Apply "${p.name}"`}>
                  <span aria-hidden="true" style={{ fontSize: 11 }}>▶</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "var(--text)", fontWeight: 500 }}>{p.name}</span>
                </button>
                <button onClick={() => deletePreset(p.name)} aria-label={`Delete preset ${p.name}`} title="Delete"
                  style={{ border: "none", background: "transparent", color: "var(--faint)", cursor: "pointer", fontSize: 11, padding: 2 }}>✕</button>
              </div>
            ))}
            {presets.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--faint)", lineHeight: 1.4 }}>Set up filters you use often, then save them here.</div>
            )}
            <div style={{ display: "flex", gap: 5, marginTop: 4 }}>
              <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") saveCurrentPreset(); }}
                placeholder="Name this view…" style={{ ...railSearch, marginBottom: 0, flex: 1 }} spellCheck={false} />
              <button onClick={saveCurrentPreset} disabled={!presetName.trim()}
                style={{ ...applyBtn, marginTop: 0, width: "auto", padding: "5px 10px", opacity: presetName.trim() ? 1 : 0.45 }}>
                Save
              </button>
            </div>
          </RailGroup>

          <RailGroup label="View by">
            {DIMS.filter((d) => dims.includes(d.value)).map((d) => (
              <div key={d.value}>
                <CheckRow kind="radio" label={d.label} on={dim === d.value}
                  onClick={() => { setDim(d.value); setSearch(""); }} />
                {/* Day/Week/Month grain lives WITH the timeline choice. */}
                {d.value === "month" && dim === "month" && (
                  <div style={{ margin: "4px 0 6px 21px" }}>
                    <Segmented size="sm" ariaLabel="Time grain"
                      options={[{ value: "day", label: "Day" }, { value: "week", label: "Week" }, { value: "month", label: "Month" }]}
                      value={grain} onChange={(g) => setGrain(g as Granularity)} />
                  </div>
                )}
                {d.value === "time_of_day" && dim === "time_of_day" && (
                  <div style={{ margin: "4px 0 6px 21px" }}>
                    <Segmented size="sm" ariaLabel="Time of day grain"
                      options={[{ value: "part", label: "Part of day" }, { value: "hour", label: "Hour" }]}
                      value={todGrain} onChange={(g) => setTodGrain(g as "part" | "hour")} />
                  </div>
                )}
              </div>
            ))}
          </RailGroup>

          {subsMetric ? (
            <DisabledRailGroup label="Customer type" reason="Plans aren't orders — there's no first-time/repeat buyer to split by." />
          ) : (
            <RailGroup label="Customer type">
              <CheckRow label="New (first order)" on={customerType !== "returning"} onClick={() => setCustomerType((c) => (c === "" ? "returning" : c === "new" ? "" : ""))} />
              <CheckRow label="Returning (repeat)" on={customerType !== "new"} onClick={() => setCustomerType((c) => (c === "" ? "new" : c === "returning" ? "" : ""))} />
            </RailGroup>
          )}

          {subsMetric ? (
            <DisabledRailGroup label="Order type" reason="Every row here IS a subscription plan already." />
          ) : (
            <RailGroup label="Order type">
              <CheckRow kind="radio" label="All orders" on={orderType === ""} onClick={() => setOrderType("")} />
              <CheckRow kind="radio" label="One-time" on={orderType === "one_time"} onClick={() => setOrderType("one_time")} />
              <CheckRow kind="radio" label="Subscription" on={orderType === "subscription"} onClick={() => setOrderType("subscription")} />
            </RailGroup>
          )}

          {/* Revenue basis (taxes / shipping / refunds) is now the global
              gross↔net control in the top bar, shared with the cards. */}

          <CheckListGroup label="Channel" options={(channelList.data?.rows ?? []).map((r) => r.key)} selected={channels} onChange={setChannels} />

          {subsMetric ? (
            <DisabledRailGroup label="Discount codes" reason="Codes attach to orders — open a revenue or orders card to filter by them." />
          ) : (
            <CheckListGroup label="Discount codes" options={(discountList.data?.rows ?? []).map((r) => r.key)} selected={discounts} onChange={setDiscounts} />
          )}

          {/* Revenue adjustments — moved here from the top bar so the drill rail
              matches the top-level page rail (founder 2026-06-30: "keep them only
              in the filter sidebar"). Same useDisplayOpts basis the cards read. */}
          <RailGroup label="Revenue adjustments">
            <CheckRow label="Subtract refunds" sub="show what you actually kept"
              on={display.refunds === "net"} onClick={() => display.set({ refunds: display.refunds === "net" ? "gross" : "net" })} />
            <CheckRow label="Exclude sales tax" sub="tax is pass-through, not revenue"
              on={display.tax === "exclude"} onClick={() => display.set({ tax: display.tax === "exclude" ? "include" : "exclude" })} />
            <CheckRow label="Include shipping" sub="count shipping charged as revenue"
              on={display.shipping === "include"} onClick={() => display.set({ shipping: display.shipping === "include" ? "exclude" : "include" })} />
            <WholesaleToggle variant="row" />
          </RailGroup>

          {subsMetric ? (
            <DisabledRailGroup label="Refunds" reason="Refunds attach to orders, not plans — open a refund card to study them." />
          ) : (
            <RailGroup label="Refunds">
              <CheckRow kind="radio" label="Include refunded" on={refunded === ""} onClick={() => setRefunded("")} />
              <CheckRow kind="radio" label="Exclude refunded" on={refunded === "exclude"} onClick={() => setRefunded("exclude")} />
              <CheckRow kind="radio" label="Refunded only" on={refunded === "only"} onClick={() => setRefunded("only")} />
            </RailGroup>
          )}

          <HierProductFilter
            hierarchy={productHierarchy}
            grouping={filterGrouping}
            onGrouping={(g) => { setFilterGrouping(g); setProducts([]); }}
            selected={products}
            onChange={setProducts}
            catalogLoading={productSkusQ.isLoading}
            catalogLoad={productSkusLoad}
          />
          <CheckListGroup label="States" options={(stateList.data?.rows ?? []).map((r) => r.key)} selected={states} onChange={setStates} />
        </aside>
        )}
      </div>
    </div>
  );
}

/**
 * "Select all" is a real TRI-STATE shared by every rail checklist. Three states:
 *   - `[]`        → NO FILTER — everything included (query-friendly default).
 *   - `[NONE]`    → explicitly NOTHING selected, so "Select all" can DESELECT all
 *                   (founder 2026-06-28: "if everything was checked and you click
 *                   again, it deselects all"). The chart then shows no rows.
 *   - any subset  → exactly those leaves.
 * NONE is printable (Postgres rejects null bytes) and can't collide with a real
 * product/channel/state/discount — the backend just matches zero rows for it.
 */
const NONE = "__dd_none__";
interface TriState {
  allOn: boolean; noneOn: boolean; partial: boolean; count: number;
  isOn: (leaf: string) => boolean;
  current: string[];
  set: (next: string[]) => void;
  toggleLeaf: (leaf: string) => void;
  toggleAll: () => void;
}
export function makeTriState(selected: string[], onChange: (v: string[]) => void, allLeaves: string[]): TriState {
  const allOn = selected.length === 0;
  const noneOn = selected.length === 1 && selected[0] === NONE;
  const partial = !allOn && !noneOn;
  const isOn = (leaf: string): boolean => allOn || (partial && selected.includes(leaf));
  const current = allOn ? allLeaves : noneOn ? [] : selected; // the real on-leaves right now
  const set = (nextRaw: string[]): void => {
    const next = [...new Set(nextRaw.filter((x) => x !== NONE))];
    // All checked → fold to [] (no filter); none → [NONE]; otherwise the subset.
    onChange(allLeaves.length > 0 && next.length >= allLeaves.length ? [] : next.length === 0 ? [NONE] : next);
  };
  const toggleLeaf = (leaf: string): void => set(isOn(leaf) ? current.filter((x) => x !== leaf) : [...current, leaf]);
  const toggleAll = (): void => onChange(allOn ? [NONE] : []); // checked → none; else → all
  return { allOn, noneOn, partial, count: partial ? selected.length : 0, isOn, current, set, toggleLeaf, toggleAll };
}
/** The "(3)" / "(none)" suffix a checklist group shows next to its label. */
export const filterCountSuffix = (ts: TriState): string => (ts.allOn ? "" : ts.noneOn ? " (none)" : ` (${ts.count})`);

/**
 * Checkbox list with a tri-state Select all + search. Every box starts CHECKED
 * (= no filter); unchecking narrows; Select all toggles between all and none.
 */
function CheckListGroup({ label, options, selected, onChange, subOf }: { label: string; options: string[]; selected: string[]; onChange: (v: string[]) => void; subOf?: (o: string) => string | undefined }): React.ReactElement {
  const [q, setQ] = useState("");
  // Search matches the option name OR its sub-line (SKU codes), so typing a SKU finds its product.
  const shown = (q ? options.filter((o) => `${o} ${subOf?.(o) ?? ""}`.toLowerCase().includes(q.toLowerCase())) : options).slice(0, 60);
  const ts = makeTriState(selected, onChange, options);
  return (
    <RailGroup label={`${label}${filterCountSuffix(ts)}`}>
      {options.length > 8 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} style={railSearch} spellCheck={false} />
      )}
      <CheckRow label="Select all" on={ts.allOn} indeterminate={ts.partial} onClick={ts.toggleAll} />
      <div style={{ maxHeight: 180, overflowY: "auto" }}>
        {shown.map((o) => (
          <CheckRow key={o} label={o} sub={subOf?.(o)} on={ts.isOn(o)} onClick={() => ts.toggleLeaf(o)} />
        ))}
        {options.length > 60 && shown.length === 60 && (
          <div style={{ color: "var(--faint)", fontSize: 11, padding: "4px 7px" }}>Search to narrow…</div>
        )}
      </div>
    </RailGroup>
  );
}

/** Hierarchical product filter (founder 2026-06-28): a Products/SKUs toggle picks the
 *  leaf level, families are parent rows, and checking a family selects all its members
 *  (or check individual ones). Empty selection = no filter (everything). Selections are
 *  flat leaf names; the breakdown filter matches family / product / sku for each. */
export function HierProductFilter({ hierarchy, grouping, onGrouping, selected, onChange, catalogLoading = false, catalogLoad }: {
  hierarchy: ProductFamily[];
  grouping: "product" | "sku";
  onGrouping: (g: "product" | "sku") => void;
  selected: string[];
  onChange: (v: string[]) => void;
  catalogLoading?: boolean;
  catalogLoad?: PageLoadState;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const leavesOf = (f: ProductFamily): string[] => (grouping === "sku" ? f.skus : f.products);
  const allLeaves = useMemo(() => hierarchy.flatMap(leavesOf), [hierarchy, grouping]);
  const ts = makeTriState(selected, onChange, allLeaves);
  const skuDisplay = (f: ProductFamily, skuCode: string) => f.skuDisplayByCode?.[skuCode];
  const skuSub = (f: ProductFamily, skuCode: string): string | undefined => {
    const display = skuDisplay(f, skuCode);
    if (!display) return undefined;
    return [display.details, display.code].filter(Boolean).join(" · ");
  };
  const toggleFamily = (f: ProductFamily): void => {
    const fl = leavesOf(f);
    ts.set(fl.every(ts.isOn) ? ts.current.filter((x) => !fl.includes(x)) : [...ts.current, ...fl]);
  };
  const shown = (q ? hierarchy.filter((f) => {
    const displayText = grouping === "sku"
      ? f.skus.flatMap((code) => {
        const display = skuDisplay(f, code);
        return display ? [display.description, display.details, display.code] : [];
      }).join(" ")
      : "";
    return `${f.family} ${leavesOf(f).join(" ")} ${displayText}`.toLowerCase().includes(q.toLowerCase());
  }) : hierarchy).slice(0, 80);
  return (
    <RailGroup label={grouping === "sku" ? `SKUs${filterCountSuffix(ts)}` : `Products${ts.allOn ? "" : ` (${hierarchy.filter((f) => leavesOf(f).some(ts.isOn)).length} selected)`}`}>
      <div style={{ marginBottom: 8 }}>
        <Segmented size="sm" ariaLabel="Filter by" value={grouping} onChange={(v) => onGrouping(v as "product" | "sku")}
          options={[{ value: "product", label: "Products" }, { value: "sku", label: "SKUs" }]} />
      </div>
      {catalogLoad?.failed ? (
        <PageLoadError
          title="We couldn't load your product list"
          retrying={catalogLoad.retrying}
          onRetry={catalogLoad.retry}
        />
      ) : catalogLoading ? (
        <div role="status" style={{ color: "var(--body)", fontSize: 12, padding: "7px 0" }}>Loading products…</div>
      ) : (
        <>
          {allLeaves.length > 8 && (
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product or SKU…" style={railSearch} spellCheck={false} />
          )}
          <CheckRow label="Select all" on={ts.allOn} indeterminate={ts.partial} onClick={ts.toggleAll} />
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {shown.map((f) => {
              const fl = leavesOf(f);
              const onCount = fl.filter(ts.isOn).length;
              const allOn = fl.length > 0 && onCount === fl.length;
              // A single-member family (e.g. an ungrouped product) is its own leaf — no
              // redundant child row; the family row IS the selectable item.
              const expandable = fl.length > 1;
              const singleSkuCode = grouping === "sku" && fl.length === 1 ? fl[0] : undefined;
              const singleSkuDisplay = singleSkuCode ? skuDisplay(f, singleSkuCode) : undefined;
              return (
                <div key={f.family}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ flex: 1, minWidth: 0 }}><CheckRow
                    label={singleSkuDisplay?.description ?? f.family}
                    sub={singleSkuCode ? skuSub(f, singleSkuCode) : undefined}
                    on={allOn}
                    indeterminate={onCount > 0 && !allOn}
                    onClick={() => toggleFamily(f)}
                  /></div>
                  {expandable && q.trim() === "" && <button type="button" aria-label={`${expandedFamilies.has(f.family) ? "Hide" : "Show"} offers for ${f.family}`}
                    aria-expanded={q.trim() !== "" || expandedFamilies.has(f.family)}
                    style={{ border: 0, background: "transparent", color: "var(--body)", cursor: "pointer", padding: "6px 8px" }}
                    onClick={() => setExpandedFamilies((previous) => {
                      const next = new Set(previous);
                      if (next.has(f.family)) next.delete(f.family); else next.add(f.family);
                      return next;
                    })}>{q.trim() !== "" || expandedFamilies.has(f.family) ? "▾" : "▸"}</button>}
                  </div>
                  {expandable && (q.trim() !== "" || expandedFamilies.has(f.family)) && fl.map((leaf) => (
                    <div key={leaf} style={{ paddingLeft: 20 }}>
                      <CheckRow
                        label={grouping === "sku" ? skuDisplay(f, leaf)?.description ?? f.family : leaf}
                        sub={grouping === "sku" ? skuSub(f, leaf) : undefined}
                        on={ts.isOn(leaf)}
                        onClick={() => ts.toggleLeaf(leaf)}
                      />
                    </div>
                  ))}
                </div>
              );
            })}
            {hierarchy.length === 0 && <div style={{ color: "var(--faint)", fontSize: 11, padding: "4px 7px" }}>No products yet.</div>}
            {hierarchy.length > 0 && shown.length === 0 && <div style={{ color: "var(--faint)", fontSize: 11, padding: "4px 7px" }}>No products match this search.</div>}
          </div>
        </>
      )}
    </RailGroup>
  );
}

/** Visible drag handle on the rail's LEFT edge — a subtle vertical pill that
 *  brightens on hover so it's discoverable (founder 2026-06-28: "I need to be
 *  able to resize the sidebar"). Drag left to widen and read full product/SKU names. */
function RailGrip({ onGripDown }: { onGripDown: (e: React.MouseEvent) => void }): React.ReactElement {
  const [hover, setHover] = useState(false);
  return (
    <div onMouseDown={onGripDown} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title="Drag to resize" aria-hidden="true"
      style={{ position: "absolute", left: -6, top: 0, bottom: 0, width: 13, cursor: "col-resize", zIndex: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: hover ? 5 : 3, height: hover ? 48 : 36, borderRadius: 3, background: hover ? "var(--accent)" : "var(--border)", transition: "background .12s, width .12s, height .12s" }} />
    </div>
  );
}

/** Custom date range — two date inputs + Apply. End date is inclusive.
 *  Dates are UTC days, matching every other window in the app (per-account
 *  timezone bucketing is queued with the V2 timezone work — change them
 *  together or windows stop reconciling). */
function CustomRange({ win, onApply, horizontal = false }: { win: DrillWindow | null; onApply: (from: string, to: string, label: string) => void; horizontal?: boolean }): React.ReactElement {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const active = Boolean(win && !win.key && win.from);
  const disabled = !from || !to || from > to;
  const apply = (): void => {
    if (disabled) return;
    // Breakdown windows are [from, to) — bump the chosen end day so it's included.
    const end = new Date(`${to}T00:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 1);
    onApply(from, end.toISOString().slice(0, 10), `${from} → ${to}`);
  };
  // Inline single-row variant for the top window bar.
  if (horizontal) {
    return (
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11.5, color: active ? "var(--accent)" : "var(--faint)", fontWeight: 600 }}>
          {active ? `Custom: ${win?.label ?? ""}` : "Custom"}
        </span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...dateInput, flex: "none", width: 132 }} aria-label="From date" />
        <span style={{ color: "var(--faint)", fontSize: 11 }}>→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...dateInput, flex: "none", width: 132 }} aria-label="To date" />
        <button onClick={apply} disabled={disabled} style={{ ...applyBtn, marginTop: 0, width: "auto", padding: "5px 12px", opacity: disabled ? 0.45 : 1 }}>Apply</button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11, color: active ? "var(--accent)" : "var(--faint)", fontWeight: 600, marginBottom: 4 }}>
        {active ? `Custom: ${win?.label ?? ""}` : "Custom range"}
      </div>
      <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={dateInput} aria-label="From date" />
        <span style={{ color: "var(--faint)", fontSize: 11 }}>→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={dateInput} aria-label="To date" />
      </div>
      <button onClick={apply} disabled={!from || !to || from > to}
        style={{ ...applyBtn, opacity: !from || !to || from > to ? 0.45 : 1 }}>
        Apply
      </button>
    </div>
  );
}

/** A toggle group that doesn't apply to the current measure — visible, muted,
 *  and says WHY (a silently missing toggle reads as a bug; a labeled one
 *  reads as a decision). */
function DisabledRailGroup({ label, reason }: { label: string; reason: string }): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, borderTop: "1px solid var(--hair)", paddingTop: 12, opacity: 0.55 }}>
      <div style={railLabel}>{label}</div>
      <div style={{ fontSize: 11.5, color: "var(--faint)", lineHeight: 1.45 }}>{reason}</div>
    </div>
  );
}

export function RailGroup({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, borderTop: "1px solid var(--hair)", paddingTop: 12 }}>
      <div style={railLabel}>{label}</div>
      {children}
    </div>
  );
}

export function CheckRow({ label, on, onClick, kind = "checkbox", sub, indeterminate = false }: { label: string; on: boolean; onClick: () => void; kind?: "checkbox" | "radio"; sub?: string; indeterminate?: boolean }): React.ReactElement {
  label = readableSourceLabel(label);
  sub = sub === undefined ? undefined : readableSourceLabel(sub);
  const filled = on || indeterminate;
  return (
    <button onClick={onClick} className="dd-drill-row" style={{ ...checkRow, alignItems: sub ? "flex-start" : "center" }} aria-pressed={on} title={sub ? `${label}\n${sub}` : label}>
      {kind === "radio" ? (
        <span style={{ width: 13, height: 13, borderRadius: "50%", flexShrink: 0, marginTop: sub ? 2 : 0, border: on ? "4px solid var(--accent)" : "1.5px solid var(--border)", background: "var(--surface)" }} />
      ) : (
        <span style={{
          width: 13, height: 13, borderRadius: 3.5, flexShrink: 0, marginTop: sub ? 2 : 0,
          border: filled ? "none" : "1.5px solid var(--border)",
          background: filled ? "var(--accent)" : "var(--surface)",
          color: "var(--accent-contrast)", fontSize: 10, lineHeight: "13px", textAlign: "center", fontWeight: 700,
        }}>{on ? "✓" : indeterminate ? "–" : ""}</span>
      )}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", color: on ? "var(--text)" : "var(--body)", fontWeight: on ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: sub ? "normal" : "nowrap", lineHeight: 1.3 }}>{label}</span>
        {sub && <span className="dd-num" style={{ display: "block", fontSize: 10, color: "var(--faint)", marginTop: 1, lineHeight: 1.3 }}>{sub}</span>}
      </span>
    </button>
  );
}

function Th({ children, onClick, active, dir, align = "right" }: { children: React.ReactNode; onClick: () => void; active: boolean; dir: "asc" | "desc"; align?: "left" | "right" }): React.ReactElement {
  return (
    <th onClick={onClick}
      style={{
        position: "sticky", top: 0, background: "var(--surface)", textAlign: align,
        padding: "9px 12px", fontSize: 11.5, fontWeight: 600, color: active ? "var(--text)" : "var(--faint)",
        textTransform: "uppercase", letterSpacing: "0.04em", cursor: "pointer", whiteSpace: "nowrap",
        borderBottom: "1px solid var(--border)", userSelect: "none",
      }}>
      {children}{active ? (dir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );
}

function Skeleton(): React.ReactElement {
  return (
    <div>
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "9px 0" }}>
          <div style={{ width: 170, height: 13, borderRadius: 4, background: "var(--surface-2)" }} />
          <div style={{ flex: 1, height: 18, borderRadius: 6, background: "var(--surface-2)", opacity: 0.75 - i * 0.08 }} />
        </div>
      ))}
    </div>
  );
}

// EVERY dimension needs a case — the fallback once swallowed time_of_day and
// discount, titling their drills "by product".
const dimLabel = (d: Dimension): string =>
  d === "customer_type" ? "Type"
  : d === "month" ? "Month"
  : d === "state" ? "State"
  : d === "channel" ? "Channel"
  : d === "order_type" ? "Order type"
  : d === "discount" ? "Discount code"
  : d === "time_of_day" ? "Time of day"
  : "Product";
function csvCell(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "var(--bg)", zIndex: 100,
  display: "flex", flexDirection: "column", animation: "dd-fade-in .14s ease",
};
const headerBar: React.CSSProperties = {
  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap",
  padding: "14px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)",
};
const windowBar: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
  padding: "10px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)",
};
const crumb: React.CSSProperties = { fontSize: 12, color: "var(--body)" };
const crumbLink: React.CSSProperties = { background: "transparent", border: "none", padding: 0, color: "var(--accent)", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const bodyRow: React.CSSProperties = { display: "flex", flex: 1, minHeight: 0, alignItems: "stretch" };
const chartCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", padding: "16px 18px" };
const rail: React.CSSProperties = {
  width: 248, flexShrink: 0, overflowY: "auto", padding: "18px 18px 24px",
  borderLeft: "1px solid var(--border)", background: "var(--surface)",
  display: "flex", flexDirection: "column", gap: 14,
};
/** The thin collapsed-rail strip — click anywhere to reopen the filters. */
const railStrip: React.CSSProperties = {
  width: 46, flexShrink: 0, borderLeft: "1px solid var(--border)", background: "var(--surface)",
  display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "16px 0", cursor: "pointer",
};
const railStripLabel: React.CSSProperties = {
  writingMode: "vertical-rl", fontSize: 11.5, fontWeight: 700, color: "var(--faint)",
  letterSpacing: "0.08em", textTransform: "uppercase",
};
const railIconBtn: React.CSSProperties = {
  border: "none", background: "transparent", color: "var(--body)", cursor: "pointer",
  padding: 3, display: "flex", alignItems: "center", borderRadius: 6,
};
const railTitle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--text)" };
const railLabel: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--faint)", marginBottom: 4,
};
const dateInput: React.CSSProperties = {
  flex: 1, minWidth: 0, boxSizing: "border-box", border: "1px solid var(--border)",
  background: "var(--surface-2)", color: "var(--text)", borderRadius: 7, padding: "5px 6px", fontSize: 11.5,
  colorScheme: "inherit",
};
const applyBtn: React.CSSProperties = {
  marginTop: 6, width: "100%", border: "1px solid var(--border)", background: "var(--surface-2)",
  color: "var(--text)", borderRadius: 7, padding: "5px 0", fontSize: 11.5, fontWeight: 700, cursor: "pointer",
};
const railSearch: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", background: "var(--surface-2)",
  color: "var(--text)", borderRadius: 7, padding: "6px 9px", fontSize: 12, marginBottom: 4,
};
const checkRow: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "4.5px 7px", margin: "0 -7px",
  border: "none", background: "transparent", borderRadius: 7, cursor: "pointer", fontSize: 12.5, textAlign: "left", width: "calc(100% + 14px)",
};
const clearAllBtn: React.CSSProperties = { border: "none", background: "transparent", color: "var(--accent)", fontSize: 11.5, fontWeight: 600, cursor: "pointer", padding: 0 };
const ghostBtn: React.CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)",
  borderRadius: "var(--r-sm)", padding: "7px 12px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
};
const closeBtn: React.CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)",
  width: 32, height: 32, borderRadius: "var(--r-sm)", cursor: "pointer", fontSize: 13,
};
const searchStyle: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", background: "var(--surface-2)",
  color: "var(--text)", borderRadius: 8, padding: "8px 12px", fontSize: 13,
};
const centerBox: React.CSSProperties = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: "64px 0" };
const barRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "7px 4px", cursor: "pointer", borderRadius: 6 };
const barLabel: React.CSSProperties = { width: 210, color: "var(--text)", fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const barTrack: React.CSSProperties = { flex: 1, height: 22, background: "var(--surface-2)", borderRadius: 6, overflow: "hidden" };
const shareCol: React.CSSProperties = { width: 52, textAlign: "right", color: "var(--body-faint)", fontSize: 11.5 };
const barVal: React.CSSProperties = { width: 108, textAlign: "right", color: "var(--num)", fontSize: 13, fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 12px", textAlign: "right", color: "var(--body)", borderBottom: "1px solid var(--hair)", whiteSpace: "nowrap" };
const tdKey: React.CSSProperties = { padding: "8px 12px", textAlign: "left", color: "var(--text)", fontWeight: 500, borderBottom: "1px solid var(--hair)", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
